import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { PageLoadingSpinner } from 'ui_kit_qmd'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { logout } from '@/app/authState'
import { LOGIN_SUCCESS } from '@/store/home/HomeConstants'
import { getAdminProfile } from '@/store/accountSettings/AccountSettingsThunk'
import { getFlags } from '@/store/flags/FlagsActions'
import { HOME_PAGE_ROUTE } from '@/utils/constants'
import env from '@/env.json'

const RefreshTokenResponse = z.object({ AccessToken: z.string() })

const FETCH_TIMEOUT_MS = 10_000

function parseParamsFromSearch(searchParams: URLSearchParams) {
  const accessToken = searchParams.get('accessToken') ?? ''
  const rawRedirectTo = searchParams.get('redirectTo') ?? ''
  const redirectTo = /^(https?:|javascript:|vbscript:|data:|\/\/)/.test(rawRedirectTo) ? '' : rawRedirectTo

  const splitAt = redirectTo.indexOf('?')
  const redirectPath = splitAt === -1 ? redirectTo : redirectTo.slice(0, splitAt)
  const redirectSearch = splitAt === -1 ? '' : redirectTo.slice(splitAt + 1)
  const mergedParams = new URLSearchParams(redirectSearch)
  for (const [key, value] of searchParams.entries()) {
    if (key.toLowerCase() !== 'accesstoken' && key.toLowerCase() !== 'redirectto') {
      mergedParams.set(key, value)
    }
  }
  const queryString = mergedParams.toString()
  const cleanPath = redirectPath.replace(/^\//, '')
  const fullLocation = queryString ? `${cleanPath}?${queryString}` : cleanPath

  return { accessToken, location: fullLocation }
}

export function AuthValidate() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const parsedRef = useRef(parseParamsFromSearch(searchParams))

  useEffect(() => {
    const { accessToken, location } = parsedRef.current

    if (!accessToken) {
      navigate(HOME_PAGE_ROUTE, { replace: true })
      return
    }

    // Purges the outgoing admin's records but does not reload — the token exchange below
    // is already in flight and a navigation would abandon it. See QMD-4332.
    dispatch(logout('session-replaced'))

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    async function authenticate() {
      try {
        void getFlags().catch(() => {})

        const response = await fetch(`${env.baseURL}api/v1/auth/refreshtoken`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ AccessToken: accessToken }),
          signal: controller.signal,
        })

        if (!response.ok) {
          navigate(HOME_PAGE_ROUTE, { replace: true })
          return
        }

        const result = RefreshTokenResponse.safeParse(await response.json())

        if (!result.success) {
          navigate(HOME_PAGE_ROUTE, { replace: true })
          return
        }

        dispatch({ type: LOGIN_SUCCESS, payload: { AccessToken: result.data.AccessToken } })
        const profileAction = getAdminProfile()
        if (profileAction) {
          dispatch(profileAction)
        }

        navigate(location ? `/${location}` : HOME_PAGE_ROUTE, { replace: true })
      } catch {
        if (!controller.signal.aborted) {
          navigate(HOME_PAGE_ROUTE, { replace: true })
        }
      } finally {
        clearTimeout(timeoutId)
      }
    }

    authenticate()

    return () => controller.abort()
  }, [dispatch, navigate])

  return (
    <div data-testid='pw_auth-validate-loading'>
      <PageLoadingSpinner />
    </div>
  )
}
